import {
  Stack,
  Group,
  Title,
  Badge,
  Button,
  Select,
  ActionIcon,
  Text,
  Box,
  Divider,
  Grid,
  Popover,
  Tabs,
  TextInput,
  Modal,
  TagsInput,
} from "@mantine/core";
import { Plus, LayoutTemplate, Info, AlertCircle, Trash2, FileStack } from "lucide-react";
import { CsvSchema, SlipTypeConfig } from "../services/csvSettingsService";
import { useCsvColumns } from "../hooks/useCsvColumns";
import { SampleAnalyzer } from "./csv/SampleAnalyzer";
import { TemplateModal } from "./csv/TemplateModal";
import { CsvColumnEditor } from "./csv/CsvColumnEditor";
import { CsvConfigurationHints } from "./csv/CsvConfigurationHints";
import { OcrPromptSettings } from "./csv/OcrPromptSettings";
import { EmptyColumnsGuide } from "./csv/EmptyColumnsGuide";
import { useCsvSampleAnalysis } from "./useCsvSampleAnalysis";
import { useSlipTypeConfiguration } from "./useSlipTypeConfiguration";
import { useCsvTemplateSelection } from "./useCsvTemplateSelection";

interface CsvConfigurationPanelProps {
  value: CsvSchema;
  onChange: (value: CsvSchema) => void;
  onSave?: () => void;
  loading?: boolean;
  onOpenTemplateModal?: () => void;
  /** 伝票種別ごとの個別設定 */
  slipTypeConfigs?: Record<string, SlipTypeConfig>;
  /** 伝票種別設定変更コールバック */
  onChangeSlipTypeConfigs?: (configs: Record<string, SlipTypeConfig>) => void;
}

export function CsvConfigurationPanel({
  value,
  onChange,
  onSave,
  loading = false,
  slipTypeConfigs,
  onChangeSlipTypeConfigs,
}: CsvConfigurationPanelProps) {
  const {
    slipTypeNames,
    selectedSlipType,
    setSelectedSlipType,
    addSlipTypeModalOpen,
    setAddSlipTypeModalOpen,
    newSlipTypeName,
    setNewSlipTypeName,
    hasSlipTypeSupport,
    currentSlipConfig,
    activeCsvSchema,
    activeOcrConfig,
    handleActiveCsvChange,
    handleActiveOcrConfigChange,
    handleAddSlipType,
    handleDeleteSlipType,
    slipTypeCsvColumns,
  } = useSlipTypeConfiguration(slipTypeConfigs, onChangeSlipTypeConfigs);
  const {
    selectedFile,
    analyzing,
    analysisResult,
    analysisPromptUsed,
    previewUrl,
    errorDate,
    handleFileSelect,
    executeAnalysis,
  } = useCsvSampleAnalysis(activeOcrConfig);
  const {
    templates,
    templateModalOpen,
    setTemplateModalOpen,
    selectedTemplateId,
    setSelectedTemplateId,
    fetchingTemplates,
    loadTemplates,
    handleApplyTemplate,
  } = useCsvTemplateSelection(
    value,
    onChange,
    hasSlipTypeSupport,
    selectedSlipType,
    activeCsvSchema,
    handleActiveCsvChange
  );

  // カラム操作フック
  const { addColumn, updateColumn, removeColumn, moveColumn } = useCsvColumns(value, onChange);

  // タブで使う列操作関数（デフォルト or 種別固有を切替）
  const activeColumns =
    selectedSlipType === "__default__"
      ? { addColumn, updateColumn, removeColumn, moveColumn }
      : slipTypeCsvColumns;

  // AI読取項目セレクトBOXからパスを設定するハンドラ
  // activeColumns.updateColumn を使用して、選択中の伝票種別に応じた正しいカラムを更新する
  const setPathToColumn = (index: number, path: string) => {
    activeColumns.updateColumn(index, { jsonPath: path });
  };

  return (
    <Stack gap="md">
      {/* 伝票種別タブ（サポートされている場合のみ表示） */}
      {hasSlipTypeSupport && (
        <>
          <Stack gap={4}>
            <Group gap="xs" align="center">
              <FileStack size={16} />
              <Text size="sm" fw={600}>
                伝票種別
              </Text>
              {slipTypeNames.length > 0 && (
                <Badge variant="light" color="blue" size="sm">
                  {slipTypeNames.length}件の個別設定
                </Badge>
              )}
            </Group>
            <Tabs
              value={selectedSlipType}
              onChange={setSelectedSlipType}
              variant="pills"
              radius="xl"
            >
              <Tabs.List>
                {slipTypeNames.map((name) => (
                  <Tabs.Tab
                    key={name}
                    value={name}
                    rightSection={
                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSlipType(name);
                        }}
                        aria-label={`種別「${name}」を削除`}
                      >
                        <Trash2 size={12} />
                      </ActionIcon>
                    }
                  >
                    {name}
                  </Tabs.Tab>
                ))}
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<Plus size={12} />}
                  onClick={() => setAddSlipTypeModalOpen(true)}
                >
                  追加
                </Button>
              </Tabs.List>
            </Tabs>
          </Stack>
          <Divider variant="dashed" />

          {/* エイリアス入力（デフォルトタブ以外で表示） */}
          {selectedSlipType && selectedSlipType !== "__default__" && (
            <TagsInput
              label={`「${selectedSlipType}」のエイリアス`}
              description="AIが検出した種別名がエイリアスと一致する場合、この種別として処理されます"
              placeholder="エイリアスを入力してEnter"
              value={currentSlipConfig?.aliases || []}
              onChange={(newAliases) => {
                if (slipTypeConfigs && onChangeSlipTypeConfigs) {
                  onChangeSlipTypeConfigs({
                    ...slipTypeConfigs,
                    [selectedSlipType]: {
                      ...slipTypeConfigs[selectedSlipType],
                      aliases: newAliases,
                    },
                  });
                }
              }}
            />
          )}
        </>
      )}

      <Group justify="space-between">
        <Group>
          <Title order={5}>
            {selectedSlipType && selectedSlipType !== "__default__"
              ? `CSV出力項目定義【${selectedSlipType}】`
              : "CSV出力項目定義"}
          </Title>
          <Badge variant="dot" color={activeCsvSchema.encoding === "shift_jis" ? "orange" : "blue"}>
            {activeCsvSchema.encoding === "shift_jis" ? "Shift_JIS" : "UTF-8 (BOM付)"}
          </Badge>
        </Group>
        <Group>
          {onSave && (
            <Button onClick={onSave} loading={loading} size="xs">
              保存
            </Button>
          )}
        </Group>
      </Group>

      <Grid gutter="xl" align="flex-start" mt="md">
        <Grid.Col span={{ base: 12, xl: 7 }}>
          <Stack gap="sm">
            <CsvConfigurationHints />

            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Title order={4} size="h5">
                  CSV項目設定
                </Title>
                {activeCsvSchema.columns.length === 0 && (
                  <AlertCircle size={16} color="var(--mantine-color-red-6)" />
                )}
              </Group>
              <Select
                size="xs"
                label={
                  <Group gap={4}>
                    <Text component="span" size="xs" fw={500}>
                      文字コード
                    </Text>
                    <Popover width={320} position="bottom-end" shadow="md" withArrow>
                      <Popover.Target>
                        <ActionIcon variant="transparent" size="xs" color="gray" aria-label="詳細">
                          <Info size={12} />
                        </ActionIcon>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Stack gap="xs">
                          <Text size="xs" fw={700}>
                            文字コードの選び方
                          </Text>
                          <Box>
                            <Text size="xs" fw={700} c="orange.8">
                              Shift_JIS
                            </Text>
                            <Text size="xs">
                              Excelで直接開く場合や、古い基幹システムに取り込む場合に推奨されます。
                              <br />
                              ※丸数字（①など）や一部の環境依存文字が文字化けする可能性があります。
                            </Text>
                          </Box>
                          <Divider my={4} />
                          <Box>
                            <Text size="xs" fw={700} c="blue.8">
                              UTF-8 (BOM付)
                            </Text>
                            <Text size="xs">
                              最新のシステムやWebサービス間での連携に最適です。
                              <br />
                              あらゆる文字を扱えますが、対応していない古いシステムでは読み込めない場合があります。
                              Excelで開く際は「BOM付き」であれば文字化けしません。
                            </Text>
                          </Box>
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                }
                data={[
                  { value: "utf-8", label: "UTF-8 (BOM付)" },
                  { value: "shift_jis", label: "Shift_JIS" },
                ]}
                value={activeCsvSchema.encoding}
                onChange={(val) =>
                  handleActiveCsvChange({
                    ...activeCsvSchema,
                    encoding: val as "utf-8" | "shift_jis",
                  })
                }
                style={{ width: 140 }}
              />
            </Group>

            {activeCsvSchema.columns.length === 0 ? (
              <EmptyColumnsGuide
                onAddColumn={activeColumns.addColumn}
                onOpenTemplateModal={() => {
                  setTemplateModalOpen(true);
                  loadTemplates();
                }}
              />
            ) : (
              <>
                <CsvColumnEditor
                  columns={activeCsvSchema.columns}
                  analysisResult={analysisResult}
                  onUpdateColumn={activeColumns.updateColumn}
                  onRemoveColumn={activeColumns.removeColumn}
                  onMoveColumn={activeColumns.moveColumn}
                  onSetPath={setPathToColumn}
                />

                <Group>
                  <Button
                    variant="outline"
                    leftSection={<Plus size={14} />}
                    size="xs"
                    onClick={activeColumns.addColumn}
                  >
                    項目を追加
                  </Button>
                  <Button
                    variant="default"
                    size="xs"
                    leftSection={<LayoutTemplate size={14} />}
                    onClick={() => {
                      setTemplateModalOpen(true);
                      loadTemplates();
                    }}
                  >
                    テンプレート適用
                  </Button>
                </Group>
              </>
            )}

            {activeOcrConfig && (
              <>
                <Divider my="sm" variant="dashed" />
                <OcrPromptSettings
                  ocrConfig={activeOcrConfig}
                  onChangeOcrConfig={handleActiveOcrConfigChange}
                />
              </>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, xl: 5 }}>
          <SampleAnalyzer
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            analyzing={analyzing}
            onExecuteAnalysis={executeAnalysis}
            previewUrl={previewUrl}
            analysisResult={analysisResult}
            errorMessage={errorDate}
            promptUsed={analysisPromptUsed}
          />
        </Grid.Col>
      </Grid>

      {/* テンプレート選択モーダル */}
      <TemplateModal
        opened={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        templates={templates}
        loading={fetchingTemplates}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={setSelectedTemplateId}
        onApply={handleApplyTemplate}
      />

      {/* 伝票種別追加モーダル */}
      {hasSlipTypeSupport && (
        <Modal
          opened={addSlipTypeModalOpen}
          onClose={() => {
            setAddSlipTypeModalOpen(false);
            setNewSlipTypeName("");
          }}
          title="伝票種別を追加"
          size="sm"
        >
          <Stack gap="md">
            <TextInput
              label="伝票種別名"
              placeholder="例: 返品伝票、通常発注"
              value={newSlipTypeName}
              onChange={(e) => setNewSlipTypeName(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddSlipType();
              }}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setAddSlipTypeModalOpen(false)}>
                キャンセル
              </Button>
              <Button
                onClick={handleAddSlipType}
                disabled={
                  !newSlipTypeName.trim() ||
                  (slipTypeConfigs && !!slipTypeConfigs[newSlipTypeName.trim()])
                }
              >
                追加
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
}
